import React from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Breadcrumb } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { useAppStore } from '../stores/app.store';
import { defaultMenus, MenuRoute } from '../config/menu';
import { filterMenusByRole } from '../utils/permission';

const { Header, Sider, Content } = Layout;

/**
 * 管理后台布局
 *
 * 使用 Ant Design Layout 组件构建：
 * - Header: 顶部导航栏（Logo、侧边栏折叠按钮、用户头像菜单）
 * - Sider: 侧边栏菜单（根据角色权限过滤）
 * - Content: 主内容区域（面包屑 + 页面内容）
 */
const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar } = useAppStore();

  // 根据角色过滤菜单
  const filteredMenus = filterMenusByRole(
    defaultMenus,
    user?.role,
  );

  // 将菜单配置转为 Ant Design Menu items
  const menuItems = React.useMemo(() => {
    return filteredMenus.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
      children: item.children?.map((child) => ({
        key: child.key,
        label: child.label,
      })),
    }));
  }, [filteredMenus]);

  // 当前选中菜单
  const selectedKeys = React.useMemo(() => {
    const path = location.pathname;
    // 找到匹配的菜单项
    const findKey = (menus: MenuRoute[]): string => {
      for (const menu of menus) {
        if (menu.path && path.startsWith(menu.path)) {
          return menu.key;
        }
        if (menu.children) {
          const childKey = findKey(menu.children);
          if (childKey) return childKey;
        }
      }
      return 'dashboard';
    };
    return [findKey(defaultMenus)];
  }, [location.pathname]);

  // 打开子菜单的 keys
  const openKeys = React.useMemo(() => {
    const path = location.pathname;
    const keys: string[] = [];
    for (const menu of defaultMenus) {
      if (menu.children) {
        const hasMatch = menu.children.some(
          (child) => child.path && path.startsWith(child.path),
        );
        if (hasMatch) {
          keys.push(menu.key);
        }
      }
    }
    return keys;
  }, [location.pathname]);

  // 面包屑
  const breadcrumbItems = React.useMemo(() => {
    const items: { title: string }[] = [{ title: '首页' }];
    for (const menu of defaultMenus) {
      if (menu.children) {
        for (const child of menu.children) {
          if (child.path && location.pathname.startsWith(child.path)) {
            items.push({ title: menu.label });
            items.push({ title: child.label });
            return items;
          }
        }
      } else if (menu.path && location.pathname.startsWith(menu.path)) {
        items.push({ title: menu.label });
        return items;
      }
    }
    return items;
  }, [location.pathname]);

  // 菜单点击导航
  const handleMenuClick = ({ key }: { key: string }) => {
    const findPath = (menus: MenuRoute[]): string | undefined => {
      for (const menu of menus) {
        if (menu.key === key && menu.path) {
          return menu.path;
        }
        if (menu.children) {
          const childPath = findPath(menu.children);
          if (childPath) return childPath;
        }
      }
      return undefined;
    };
    const path = findPath(defaultMenus);
    if (path) {
      navigate(path);
    }
  };

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={sidebarCollapsed}
        theme="dark"
        width={220}
      >
        {/* Logo */}
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: sidebarCollapsed ? '14px' : '16px',
            fontWeight: 'bold',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {sidebarCollapsed ? '人才周' : '人才周管理平台'}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={openKeys}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              type="text"
              icon={
                sidebarCollapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={toggleSidebar}
            />
          </div>

          <Dropdown
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            placement="bottomRight"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: '0 12px',
              }}
            >
              <Avatar size="small" icon={<UserOutlined />} />
              <span>{user?.name || '未登录'}</span>
            </div>
          </Dropdown>
        </Header>

        <Content style={{ margin: 16 }}>
          <Breadcrumb
            items={breadcrumbItems}
            style={{ marginBottom: 16 }}
          />
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              minHeight: 'calc(100vh - 160px)',
            }}
          >
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
